import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import AuthWatcher from './components/AuthWatcher.jsx'
import AuthGate from './components/AuthGate.jsx'
import Layout from './components/Layout.jsx'
import PatientLayout from './components/PatientLayout.jsx'
import Huddle from './pages/Huddle.jsx'
import Appointments from './pages/Appointments.jsx'
import AppointmentPrint from './pages/AppointmentPrint.jsx'
import WhtCertificate from './pages/WhtCertificate.jsx'
import Treatments from './pages/Treatments.jsx'
import CallList from './pages/CallList.jsx'
import Reports from './pages/Reports.jsx'
import RevenueReport from './pages/reports/RevenueReport.jsx'
import ReimbursementHub from './pages/reports/ReimbursementHub.jsx'
import SsoImportPrint from './pages/reports/SsoImportPrint.jsx'
import FollowUpPrint from './pages/reports/FollowUpPrint.jsx'
import ClaimImportRun from './pages/reports/ClaimImportRun.jsx'
import Upsale from './pages/reports/Upsale.jsx'
import Outstanding from './pages/reports/Outstanding.jsx'
import NewCase from './pages/reports/NewCase.jsx'
import Implant from './pages/reports/Implant.jsx'
import FollowUps from './pages/FollowUps.jsx'
import Recall from './pages/follow-ups/Recall.jsx'
import Ticket from './pages/follow-ups/Ticket.jsx'
import Stock from './pages/Stock.jsx'
import StockCut from './pages/stock/StockCut.jsx'
import StockTransfer from './pages/stock/StockTransfer.jsx'
import InventoryList from './pages/stock/InventoryList.jsx'
import InventoryDetail from './pages/stock/InventoryDetail.jsx'
import Purchase from './pages/Purchase.jsx'
import Lab from './pages/Lab.jsx'
import Manager from './pages/Manager.jsx'
import Expense from './pages/Expense.jsx'
import Dentist from './pages/Dentist.jsx'
import DentistLayout from './components/DentistLayout.jsx'
import DentistIncome from './pages/dentist/DentistIncome.jsx'
import Assets from './pages/Assets.jsx'
import AssetDetail from './pages/AssetDetail.jsx'
import AssetCheck from './pages/AssetCheck.jsx'
import Promotion from './pages/Promotion.jsx'
import KitGallery from './kit/KitGallery.jsx'
import RequireManager from './components/RequireManager.jsx'
import RequireStaff from './components/RequireStaff.jsx'
import RequirePromotion from './components/RequirePromotion.jsx'
import EmployeeLayout from './components/EmployeeLayout.jsx'
import ScheduleLayout from './components/ScheduleLayout.jsx'
import DoctorSchedule from './pages/schedule/DoctorSchedule.jsx'
import DoctorScheduleLayout from './components/schedule/DoctorScheduleLayout.jsx'
import ShiftRequest from './pages/schedule/ShiftRequest.jsx'
import DoctorLeave from './pages/schedule/DoctorLeave.jsx'
import Broadcast from './pages/broadcast/Broadcast.jsx'
import EmployeeSchedule from './pages/schedule/EmployeeSchedule.jsx'
import EmployeeList from './pages/employee/EmployeeList.jsx'
import EmployeeClockin from './pages/employee/EmployeeClockin.jsx'
import EmployeeLeave from './pages/employee/EmployeeLeave.jsx'
import EmployeeRecurring from './pages/employee/EmployeeRecurring.jsx'
import EmployeeSalary from './pages/employee/EmployeeSalary.jsx'
import MeLayout from './components/MeLayout.jsx'
import MeSetting from './pages/me/MeSetting.jsx'
import MeClockin from './pages/me/MeClockin.jsx'
import MeSchedule from './pages/me/MeSchedule.jsx'
import MeSalary from './pages/me/MeSalary.jsx'
import MeEvaluation from './pages/me/MeEvaluation.jsx'
import MeLeave from './pages/me/MeLeave.jsx'
import MeHrLayout from './components/me/MeHrLayout.jsx'
import MeWarning from './pages/me/MeWarning.jsx'
import MeDocument from './pages/me/MeDocument.jsx'
import LeaveLayout from './components/employee/LeaveLayout.jsx'
import DocsLayout, { DocsIndex } from './components/employee/DocsLayout.jsx'
import EmployeeLeaveRequests from './pages/employee/EmployeeLeaveRequests.jsx'
import EmployeeDocument from './pages/employee/EmployeeDocument.jsx'
import EmployeeResignation from './pages/employee/EmployeeResignation.jsx'
import EmployeeEvaluation from './pages/employee/EmployeeEvaluation.jsx'
import Statistic from './pages/Statistic.jsx'
import PatientInfo from './pages/patient/PatientInfo.jsx'
import PatientAppointments from './pages/patient/PatientAppointments.jsx'
import PatientTreatments from './pages/patient/PatientTreatments.jsx'
import ChartLayout from './components/ChartLayout.jsx'
import PatientChartHistory from './pages/patient/PatientChartHistory.jsx'
import PatientChartHistoryDetail from './pages/patient/PatientChartHistoryDetail.jsx'
import PatientChartSummary from './pages/patient/PatientChartSummary.jsx'
import PatientChartFile from './pages/patient/PatientChartFile.jsx'
import PatientProfile from './pages/patient/PatientProfile.jsx'
import PatientLab from './pages/patient/PatientLab.jsx'
import PatientPromotion from './pages/patient/PatientPromotion.jsx'
import PatientFollowUps from './pages/patient/PatientFollowUps.jsx'
import PatientOldVersion from './pages/patient/PatientOldVersion.jsx'
import PatientLog from './pages/patient/PatientLog.jsx'
import ConsentForm from './pages/ConsentForm.jsx'

/** <Navigate> that PRESERVES the query string and stamps the destination tab. */
function KeepQuery({ to, tab }) {
  const { search } = useLocation()
  const p = new URLSearchParams(search)
  p.set('tab', tab)
  return <Navigate to={`${to}?${p}`} replace />
}

export default function App() {
  // Patient-facing consent form — PUBLIC, so it must sit outside AuthGate /
  // AuthWatcher (both force-logout a visitor with no session).
  const { pathname } = useLocation()
  if (pathname.startsWith('/consent/')) {
    return (
      <Routes>
        <Route path="/consent/:chartId/:token" element={<ConsentForm />} />
      </Routes>
    )
  }

  return (
    <AuthGate>
    <AuthWatcher />
    <Routes>
      <Route element={<Layout />}>
        <Route path="/"            element={<Navigate to="/appointments" replace />} />
        <Route path="/huddle"       element={<Huddle />} />
        <Route path="/appointments" element={<Appointments />} />
        <Route path="/treatments"   element={<Treatments />} />
        <Route path="/calls"        element={<CallList />} />
        <Route path="/reports"      element={<Reports />} />
        <Route path="/reports/revenue" element={<RevenueReport />} />
        <Route path="/reports/reimbursement" element={<ReimbursementHub />} />
        {/* Merged INTO the tabs above. The old paths stay alive — bookmarks, and the claim
            courier's own operator-facing log strings in PDF/claim_courier/Code.gs.
            ⚠️ A plain <Navigate> DROPS the query string, and these URLs now carry filters. */}
        <Route path="/reports/claim-imports" element={<KeepQuery to="/reports/reimbursement" tab="insurer" />} />
        <Route path="/reports/sso-import" element={<KeepQuery to="/reports/reimbursement" tab="sso" />} />
        {/* ⚠️ THE RUN DETAIL DOES NOT MOVE. Four Pages-Function response bodies
            (functions/api/claim-import/_stage.js) and six operator-facing log strings in
            PDF/claim_courier/Code.gs name this exact URL — and the courier lives outside
            this repo with no clasp, so "tidying" it means hand-pasting Code.gs and cutting
            a new version. Not worth a cross-repo manual deploy. */}
        <Route path="/reports/claim-imports/:id" element={<ClaimImportRun />} />
        <Route path="/reports/upsale" element={<Upsale />} />
        <Route path="/reports/outstanding" element={<Outstanding />} />
        <Route path="/reports/newcase" element={<NewCase />} />
        <Route path="/reports/implant" element={<Implant />} />
        <Route path="/follow-ups"   element={<FollowUps />} />
        <Route path="/follow-ups/recall" element={<Recall />} />
        <Route path="/follow-ups/ticket" element={<Ticket />} />
        <Route path="/stock"         element={<Stock />} />
        <Route path="/stock/cut"     element={<StockCut />} />
        {/* ประวัติ merged INTO /stock/cut (it is now the default view there);
            keep the old path alive so existing links/bookmarks don't 404. */}
        <Route path="/stock/records" element={<Navigate to="/stock/cut" replace />} />
        <Route path="/stock/transfer"   element={<StockTransfer />} />
        <Route path="/stock/items"      element={<InventoryList />} />
        <Route path="/stock/items/:id"  element={<InventoryDetail />} />
        <Route path="/purchase"      element={<Purchase />} />
        <Route path="/lab"          element={<Lab />} />
        <Route path="/manager" element={<RequireManager />}>
          <Route index            element={<Manager />} />
          <Route path="expense"   element={<Expense />} />
          <Route path="dentist" element={<DentistLayout />}>
            <Route index           element={<Navigate to="list" replace />} />
            <Route path="list"     element={<Dentist />} />
            <Route path="income"   element={<DentistIncome />} />
            {/* ประกาศ — a TAB here rather than its own /manager/broadcast page
                (owner's call, 2026-08-20). ⚠️ The Manager area admits Stock
                Manager and REFUSES HR, the opposite of the RequireStaff gate
                ประกาศ sat behind before; DentistLayout hides the tab from Stock
                Manager because its /api/hr/broadcast* proxies would 403 them. */}
            <Route path="broadcast" element={<Broadcast />} />
          </Route>
          <Route path="assets"        element={<Assets />} />
          <Route path="assets/:id"    element={<AssetDetail />} />
          {/* QR scan lands here: one-screen "still in the clinic" proof-of-existence check */}
          <Route path="assets/:id/check" element={<AssetCheck />} />
        </Route>
        {/* พนักงาน / Employee-HR — SEPARATE gate from /manager: RequireStaff lets
            HR in (not a "manager") and keeps Stock Manager out. Sibling route so
            React Router matches /manager/employee/* here, not under RequireManager. */}
        <Route path="/manager/employee" element={<RequireStaff />}>
          <Route element={<EmployeeLayout />}>
            <Route index               element={<Navigate to="list" replace />} />
            <Route path="list"      element={<EmployeeList />} />
            <Route path="clockin"   element={<EmployeeClockin />} />
            {/* การลา carries a SECOND tab row: the ledger of leave already taken,
                plus the queue of requests nobody has decided. Nested rather than a
                sibling tab because approving a request WRITES the rows the ledger
                counts — they are two views of one thing, one step apart. */}
            <Route path="leave" element={<LeaveLayout />}>
              <Route index           element={<EmployeeLeave />} />
              <Route path="requests" element={<EmployeeLeaveRequests />} />
            </Route>
            {/* เอกสาร — the two personnel documents that are not the employee record:
                หนังสือเตือน (HR/GM/Admin, gated inside DocsLayout) and ลาออก (all
                staff; the off-boarding ANSWERS inside are HR/Admin only, enforced in
                _middleware.js and again in Xano — never by hiding a tab). */}
            <Route path="docs" element={<DocsLayout />}>
              {/* DocsIndex: the register for HR/GM/Admin, else a redirect to ลาออก —
                  the index must never render the tab DocsLayout hides. */}
              <Route index              element={<DocsIndex />} />
              <Route path="resignation" element={<EmployeeResignation />} />
              {/* ทะเบียนใบรับรอง — read-only audit of what staff issued themselves.
                  HR/Admin only (the rows carry salary); enforced in _middleware.js
                  and Xano, never by the hidden tab alone. */}
              <Route path="certificate" element={<EmployeeDocument />} />
            </Route>
            <Route path="recurring" element={<EmployeeRecurring />} />
            <Route path="salary"    element={<EmployeeSalary />} />
            <Route path="evaluation" element={<EmployeeEvaluation />} />
          </Route>
        </Route>
        {/* ตั้งค่าโปรโมชั่น — SIBLING of /manager for the same reason as /manager/employee:
            RequireManager would admit Stock Manager, and the promotion catalog is the
            clinic's price list + doctor-fee split, not a stock concern. Manager/GM/Admin
            read; GM/Admin write (lib/role.js#canViewPromotion / canEditPromotion). */}
        <Route path="/manager/promotion" element={<RequirePromotion />}>
          <Route index element={<Promotion />} />
        </Route>
        {/* ตารางเวร — SIBLING of /manager for the same reason as /manager/employee:
            RequireManager would admit Stock Manager and refuse HR, and rostering
            is an HR/Manager job. Gated by RequireStaff + the middleware's
            staffPage rule; its proxies live under /api/hr/schedule/* so they
            inherit the existing /api/hr/* staff gate. */}
        <Route path="/manager/schedule" element={<RequireStaff />}>
          <Route element={<ScheduleLayout />}>
            <Route index element={<Navigate to="doctor" replace />} />
            {/* ทันตแพทย์ carries a SECOND tab row: the roster itself plus the two
                request queues that change it. Nested rather than sibling tabs
                because ขอเวร and การลา are doctor-only — as siblings they sat
                beside the employee roster and implied they applied to it too.
                Gate and proxies agree here (RequireStaff ↔ /api/hr/*), so unlike
                ประกาศ these need no extra tab gate, and HR keeps leave approval. */}
            <Route path="doctor" element={<DoctorScheduleLayout />}>
              <Route index                element={<DoctorSchedule />} />
              <Route path="shift-request" element={<ShiftRequest />} />
              <Route path="leave"         element={<DoctorLeave />} />
            </Route>
            <Route path="employee" element={<EmployeeSchedule />} />
          </Route>
        </Route>
        {/* พื้นที่ของฉัน — self-service, open to EVERY logged-in employee (no
            role gate): each /api/me/* proxy is scoped to the session's own
            employee_id, so there is nothing here to restrict. Lands on ตั้งค่า,
            the tab that is built. */}
        <Route path="/me" element={<MeLayout />}>
          <Route index             element={<Navigate to="setting" replace />} />
          <Route path="clockin"    element={<MeClockin />} />
          <Route path="schedule"   element={<MeSchedule />} />
          <Route path="salary"     element={<MeSalary />} />
          {/* ฝ่ายบุคคล — everything between the employee and HR, behind one tab with a
              nested pill row. ขอลา is the index because it is why most people open it;
              ลาออก is last because it is once in a career. Grouping these also stops
              ขอลา and ลาออก sitting side by side in the top row, which are one syllable
              apart in Thai. */}
          <Route path="hr" element={<MeHrLayout />}>
            <Route index                 element={<MeLeave />} />
            <Route path="evaluation"     element={<MeEvaluation />} />
            <Route path="warning"        element={<MeWarning />} />
            {/* ขอเอกสาร — issues itself, no queue. Sits under ฝ่ายบุคคล because that is
                where staff already go for anything between them and HR, even though
                this is the one tab there that never waits on a decision. */}
            <Route path="documents"      element={<MeDocument />} />
          </Route>
          {/* The old top-level paths, kept as redirects: they are in emails already
              sent, and a dead link in a notification is how people stop trusting them. */}
          <Route path="leave"      element={<Navigate to="/me/hr" replace />} />
          <Route path="evaluation" element={<Navigate to="/me/hr/evaluation" replace />} />
          {/* การลาออก moved into ตั้งค่า — beside the employment facts it changes, and
              nowhere near ขอลา. */}
          <Route path="hr/resignation" element={<Navigate to="/me/setting" replace />} />
          <Route path="setting"    element={<MeSetting />} />
        </Route>
        <Route path="/statistic"    element={<Statistic />} />
        {/* Shared UI kit gallery (src/kit) — review page, not in the sidebar. */}
        <Route path="/kit"          element={<KitGallery />} />
      </Route>

      {/* Standalone print views (no Layout chrome) — opened in a new tab */}
      <Route path="/appointments/print" element={<AppointmentPrint />} />
      {/* OUTSIDE <Layout> on purpose — printing from inside it prints the
          sidebar and nav too, which no .no-print inside a page can reach. */}
      <Route path="/reports/sso-import/print" element={<SsoImportPrint />} />
      <Route path="/reports/reimbursement/followup-print" element={<FollowUpPrint />} />
      <Route path="/expense/wht" element={<WhtCertificate />} />

      <Route path="/patient/:id" element={<PatientLayout />}>
        <Route index            element={<Navigate to="info" replace />} />
        <Route path="info"         element={<PatientInfo />} />
        <Route path="appointments" element={<PatientAppointments />} />
        <Route path="treatments"   element={<PatientTreatments />} />
        <Route path="chart" element={<ChartLayout />}>
          <Route index                       element={<Navigate to="history" replace />} />
          <Route path="history"              element={<PatientChartHistory />} />
          <Route path="history/:treatmentId" element={<PatientChartHistoryDetail />} />
          <Route path="summary"              element={<PatientChartSummary />} />
          <Route path="file"                 element={<PatientChartFile />} />
        </Route>
        <Route path="profile"      element={<PatientProfile />} />
        <Route path="lab"          element={<PatientLab />} />
        <Route path="promotion"    element={<PatientPromotion />} />
        <Route path="follow-ups"   element={<PatientFollowUps />} />
        <Route path="old-version"  element={<PatientOldVersion />} />
        <Route path="log"          element={<PatientLog />} />
      </Route>

      <Route path="*" element={<div className="p-8 text-slate-500">Not found.</div>} />
    </Routes>
    </AuthGate>
  )
}
